import React from "react";
import styled from "@emotion/styled";
import { useAuth } from "../../hooks/useAuth";

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeCard = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const WelcomeTitle = styled.h2`
  color: #333;
  margin-bottom: 16px;
  font-size: 28px;
`;

const WelcomeText = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;

const ActionCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ActionIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ActionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 8px 0;
`;

const ActionDescription = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0;
  line-height: 1.4;
`;

const ExternalLeadsSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const ExternalLeadsTitle = styled.h3`
  color: #333;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
`;

const ExternalLeadsText = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const CopyUrlContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const UrlInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #4a5568;
  font-size: 13px;
  font-family: monospace;
  outline: none;
`;

const CopyButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background: #3182ce;
  }
`;

interface DashboardHomeProps {
  onNavigate: (tab: string) => void;
}

export const DashboardHome: React.FC<DashboardHomeProps> = ({ onNavigate }) => {
  const { user } = useAuth();

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  };

  const getExternalLeadsUrl = () => {
    if (user?.customerId) {
      return `${getApiBaseUrl()}/leads/external/${user.customerId}`;
    }
    return '';
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getQuickActions = () => {
    const actions = [];

    if (user?.role === "admin") {
      actions.push({
        id: "customers",
        icon: "👥",
        title: "Manage Customers",
        description:
          "View and manage all customer accounts, subscriptions, and billing.",
      });
    }

    if (user?.role === "customer") {
      actions.push(
        {
          id: "leads",
          icon: "🎯",
          title: "View Leads",
          description:
            "Track and manage leads captured from your landing pages.",
        },
        {
          id: "pages",
          icon: "📄",
          title: "Landing Pages",
          description: "Create and manage your landing pages and campaigns.",
        },
        {
          id: "create-page",
          icon: "✨",
          title: "Create Page",
          description:
            "Build a new landing page with our advanced page builder.",
        }
      );
    }

    if (user?.role === "member") {
      actions.push({
        id: "leads",
        icon: "🎯",
        title: "View Leads",
        description: "Access leads assigned to you and update their status.",
      });
    }

    return actions;
  };

  const getRoleSpecificContent = () => {
    switch (user?.role) {
      case "admin":
        return {
          title: "Admin Dashboard",
          description:
            "Welcome to your admin dashboard. Manage customers, monitor system health, and oversee all operations.",
        };
      case "customer":
        return {
          title: "Welcome to Chala Flow!",
          description:
            "Create stunning landing pages, capture leads, and grow your business with our powerful platform.",
        };
      case "member":
        return {
          title: "Team Dashboard",
          description:
            "Access your assigned leads and collaborate with your team to convert prospects into customers.",
        };
      default:
        return {
          title: "Welcome to Chala Flow!",
          description:
            "Your multi-tenant SaaS platform for landing page creation and lead management.",
        };
    }
  };

  const { title, description } = getRoleSpecificContent();

  return (
    <Container>
      <WelcomeCard>
        <WelcomeTitle>{title}</WelcomeTitle>
        <WelcomeText>{description}</WelcomeText>

        {user && (
          <div
            style={{
              padding: "12px 16px",
              background: "#f7fafc",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#4a5568",
              marginTop: "16px",
            }}
          >
            Logged in as <strong>{user.name}</strong> ({user.role})
            {user.customerId && ` • Customer ID: ${user.customerId}`}
          </div>
        )}
      </WelcomeCard>

      {user?.role === 'customer' && user?.customerId && (
        <ExternalLeadsSection>
          <ExternalLeadsTitle>External Website Integration</ExternalLeadsTitle>
          <ExternalLeadsText>
            Use this URL to integrate lead capture from your external website directly into Chala Flow. 
            Copy and configure it in your external forms or lead capture systems.
          </ExternalLeadsText>
          <CopyUrlContainer>
            <UrlInput
              type="text"
              value={getExternalLeadsUrl()}
              readOnly
            />
            <CopyButton onClick={() => copyToClipboard(getExternalLeadsUrl())}>
              Copy
            </CopyButton>
          </CopyUrlContainer>
        </ExternalLeadsSection>
      )}

      {getQuickActions().length > 0 && (
        <QuickActions>
          {getQuickActions().map((action) => (
            <ActionCard key={action.id} onClick={() => onNavigate(action.id)}>
              <ActionIcon>{action.icon}</ActionIcon>
              <ActionTitle>{action.title}</ActionTitle>
              <ActionDescription>{action.description}</ActionDescription>
            </ActionCard>
          ))}
        </QuickActions>
      )}
    </Container>
  );
};
